if (!customElements.get('gift-finder')) {
	const GIFT_FINDER_ELIGIBILITY_TAG = 'gift-finder-eligible';

	class GiftFinder extends HTMLElement {
		constructor() {
			super();
			this.onDocumentClick = this.onDocumentClick.bind(this);
			this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
			this.onDelegatedClick = this.onDelegatedClick.bind(this);
			this.onViewportChange = this.onViewportChange.bind(this);
			this.onPopState = this.onPopState.bind(this);
			this.openDropdown = null;
			this.currentPage = 1;
			this.totalPages = 1;
			this.currentParams = null;
			this.uidSeed = 0;
		}

		connectedCallback() {
			if (this.initialized) return;
			this.initialized = true;

			this.searchUrl = this.dataset.searchUrl || '/search';
			this.resultsSection = this.dataset.resultsSection || 'pd-gift-finder-results';
			this.instanceId = this.dataset.instanceId || 'default';
			this.ppp = parseInt(this.dataset.ppp, 10) || 16;
			this.sort = this.dataset.sort || 'price-ascending';
			this.eligibilityTag = (this.dataset.gfEligibilityTag || GIFT_FINDER_ELIGIBILITY_TAG).trim().toLowerCase().replace(/"/g, '');
			this.unavailable = this.dataset.unavailable || 'last';
			this.loadMoreLabel = this.dataset.loadMoreLabel || '';
			this.flags = this.dataset.flags || '';

			this.submitBtn = this.querySelector('[data-gf-submit]');
			this.resultsEl = this.querySelector('[data-gf-results]');
			this.emptyEl = this.querySelector('[data-gf-empty]');
			this.noResultsEl = this.querySelector('[data-gf-no-results]');
			this.errorEl = this.querySelector('[data-gf-error]');
			this.showMoreWrap = this.querySelector('[data-gf-show-more-wrap]');
			this.showMoreLink = this.querySelector('[data-gf-show-more]');
			this.resultsPanel = this.querySelector('.gift-finder__results-panel') || this.resultsEl;
			this.boostProducts = Array.from(this.querySelectorAll('[data-gf-boost-template]')).map((template) => ({
				template,
				id: template.dataset.gfProductId || '',
				tags: this.parseJsonArray(template.dataset.gfProductTags).map((tag) => String(tag).toLowerCase()),
				variantPrices: this.parseJsonArray(template.dataset.gfVariantPrices).map(Number),
				available: template.dataset.gfProductAvailable === 'true'
			}));

			this.dropdowns = Array.from(this.querySelectorAll('[data-gf-dropdown]')).map((root, i) => this.setupDropdown(root, i));

			this.submitBtn?.addEventListener('click', () => this.search({ scroll: false }));
			this.addEventListener('click', this.onDelegatedClick);
			document.addEventListener('click', this.onDocumentClick);
			document.addEventListener('keydown', this.onDocumentKeydown);
			window.addEventListener('resize', this.onViewportChange);
			window.addEventListener('scroll', this.onViewportChange, { passive: true, capture: true });
			window.addEventListener('popstate', this.onPopState);

			this.restoreFromUrl();
		}

		disconnectedCallback() {
			document.removeEventListener('click', this.onDocumentClick);
			document.removeEventListener('keydown', this.onDocumentKeydown);
			window.removeEventListener('resize', this.onViewportChange);
			window.removeEventListener('scroll', this.onViewportChange, { capture: true });
			window.removeEventListener('popstate', this.onPopState);
			this.removeEventListener('click', this.onDelegatedClick);
		}

		/* ---------------------------------------------------------------- dropdowns */

		setupDropdown(root, index) {
			const trigger = root.querySelector('[data-gf-trigger]');
			const popover = root.querySelector('[data-gf-popover]');
			const valueEl = root.querySelector('[data-gf-value]');
			const options = Array.from(root.querySelectorAll('[data-gf-option]'));

			options.forEach((option) => {
				if (!option.id) option.id = `GiftFinderOpt-${index}-${this.uidSeed++}`;
				option.setAttribute('tabindex', '-1');
			});

			const dd = {
				root,
				trigger,
				popover,
				valueEl,
				options,
				index,
				type: root.dataset.gfType || 'tags',
				activeIndex: Math.max(0, options.findIndex((o) => o.getAttribute('aria-selected') === 'true'))
			};

			trigger?.addEventListener('click', (e) => {
				e.preventDefault();
				this.isOpen(dd) ? this.closeDropdown(dd) : this.openDropdownAt(dd);
			});

			trigger?.addEventListener('keydown', (e) => this.onTriggerKeydown(e, dd));

			options.forEach((option, i) => {
				option.addEventListener('click', (e) => {
					e.preventDefault();
					this.select(dd, i);
				});
				option.addEventListener('mousemove', () => this.setActive(dd, i));
			});

			return dd;
		}

		isOpen(dd) {
			return this.openDropdown === dd;
		}

		openDropdownAt(dd) {
			if (!dd.popover || !dd.trigger) return;
			this.dropdowns.forEach((other) => {
				if (other !== dd) this.closeDropdown(other);
			});
			dd.popover.hidden = false;
			dd.root.classList.add('is-open');
			dd.trigger.setAttribute('aria-expanded', 'true');
			this.openDropdown = dd;
			this.positionPopover(dd);

			const selected = dd.options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
			this.setActive(dd, selected > -1 ? selected : 0);
			dd.trigger.focus();
		}

		onViewportChange() {
			if (this.openDropdown) this.positionPopover(this.openDropdown);
		}

		positionPopover(dd) {
			if (!dd.popover) return;

			dd.popover.removeAttribute('style');
		}

		closeDropdown(dd, refocus) {
			if (!dd.popover || !dd.trigger) return;
			dd.popover.hidden = true;
			dd.root.classList.remove('is-open');
			dd.trigger.setAttribute('aria-expanded', 'false');
			dd.trigger.removeAttribute('aria-activedescendant');
			dd.options.forEach((o) => o.classList.remove('is-active'));
			if (this.openDropdown === dd) this.openDropdown = null;
			if (refocus) dd.trigger.focus();
		}

		setActive(dd, index) {
			if (!dd.options.length) return;
			const total = dd.options.length;
			const next = ((index % total) + total) % total;
			dd.activeIndex = next;
			dd.options.forEach((o, i) => o.classList.toggle('is-active', i === next));
			dd.trigger?.setAttribute('aria-activedescendant', dd.options[next].id);
			const active = dd.options[next];
			if (typeof active.scrollIntoView === 'function' && dd.popover && dd.popover.scrollHeight > dd.popover.clientHeight) {
				active.scrollIntoView({ block: 'nearest' });
			}
		}

		select(dd, index, options) {
			const option = dd.options[index];
			if (!option) return;
			const val = option.dataset.gfVal || '';

			dd.options.forEach((o, i) => {
				const isSelected = i === index;
				o.setAttribute('aria-selected', isSelected ? 'true' : 'false');
				o.classList.toggle('is-selected', isSelected);
			});

			if (dd.valueEl) {
				dd.valueEl.textContent = option.textContent.trim();
				dd.valueEl.classList.toggle('is-placeholder', val === '');
			}

			this.closeDropdown(dd, !options || options.refocus !== false);
			this.updateShowMore();
		}

		onTriggerKeydown(e, dd) {
			const open = this.isOpen(dd);

			if (!open) {
				if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
					e.preventDefault();
					this.openDropdownAt(dd);
				}
				return;
			}

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					this.setActive(dd, dd.activeIndex + 1);
					break;
				case 'ArrowUp':
					e.preventDefault();
					this.setActive(dd, dd.activeIndex - 1);
					break;
				case 'Home':
					e.preventDefault();
					this.setActive(dd, 0);
					break;
				case 'End':
					e.preventDefault();
					this.setActive(dd, dd.options.length - 1);
					break;
				case 'Enter':
				case ' ':
				case 'Spacebar':
					e.preventDefault();
					this.select(dd, dd.activeIndex);
					break;
				case 'Escape':
					e.preventDefault();
					this.closeDropdown(dd, true);
					break;
				case 'Tab':
					this.closeDropdown(dd);
					break;
			}
		}

		onDocumentClick(e) {
			if (!this.openDropdown) return;
			if (this.contains(e.target)) return;
			this.closeDropdown(this.openDropdown);
		}

		onDocumentKeydown(e) {
			if (e.key !== 'Escape' || !this.openDropdown) return;
			this.closeDropdown(this.openDropdown, true);
		}

		/* ------------------------------------------------------------------- state */

		getSelection(dd) {
			const selected = dd.options.find((o) => o.getAttribute('aria-selected') === 'true');
			return selected ? (selected.dataset.gfVal || '') : '';
		}

		parseJsonArray(value) {
			try {
				const parsed = JSON.parse(value || '[]');
				return Array.isArray(parsed) ? parsed : [];
			} catch {
				return [];
			}
		}

		boostProductMatchesFilters(product) {
			if (!product.id || !product.available || !product.tags.includes(this.eligibilityTag)) return false;

			return this.dropdowns.every((dd) => {
				const raw = this.getSelection(dd);
				if (!raw) return true;

				if (dd.type === 'price') {
					const [min, max] = raw.split('-').map((value) => value.trim());
					return product.variantPrices.some((price) => {
						const aboveMin = !min || Number.isNaN(Number(min)) || price >= Number(min) * 100;
						const belowMax = !max || Number.isNaN(Number(max)) || price <= Number(max) * 100;
						return aboveMin && belowMax;
					});
				}

				const alternatives = raw.split(',').map((tag) => tag.trim().toLowerCase()).filter(Boolean);
				return alternatives.some((tag) => product.tags.includes(tag));
			});
		}

		removeBoostDuplicates(grid, pinnedProductIds) {
			if (!grid || !pinnedProductIds?.length) return;
			const pinnedIds = new Set(pinnedProductIds);
			grid.querySelectorAll('[data-gf-product-id]').forEach((product) => {
				if (pinnedIds.has(product.dataset.gfProductId)) product.remove();
			});
		}

		pinBoostProducts(grid, pinnedProducts) {
			if (!grid || !pinnedProducts.length) return;
			this.removeBoostDuplicates(grid, pinnedProducts.map((product) => product.id));
			const pinnedCards = pinnedProducts
				.map((product) => product.template.content.firstElementChild?.cloneNode(true))
				.filter(Boolean);
			if (pinnedCards.length) grid.prepend(...pinnedCards);
		}

		getMatchingBoostProducts() {
			return this.boostProducts.filter((product) => this.boostProductMatchesFilters(product));
		}

		/* ------------------------------------------------------------------ request */

		buildQuery() {
			const groups = [`tag:"${this.eligibilityTag}"`];
			const priceParams = {};

			this.dropdowns.forEach((dd) => {
				const raw = this.getSelection(dd);
				if (!raw) return; // "Any..." default — contributes no constraint

				if (dd.type === 'price') {
					const parts = raw.split('-');
					const min = (parts[0] || '').trim();
					const max = (parts[1] || '').trim();
					if (min !== '' && !Number.isNaN(Number(min))) priceParams['filter.v.price.gte'] = min;
					if (max !== '' && !Number.isNaN(Number(max))) priceParams['filter.v.price.lte'] = max;
					return;
				}

				const tags = raw
					.split(',')
					.map((t) => t.trim().replace(/"/g, ''))
					.filter(Boolean);

				if (!tags.length) return;
				groups.push(tags.length === 1 ? `tag:"${tags[0]}"` : `(${tags.map((t) => `tag:"${t}"`).join(' OR ')})`);
			});

			/* Every question can be left on its "Any..." default — /search still
			   needs a query, so fall back to the documented match-all wildcard. */
			const q = groups.length ? groups.join(' AND ') : '*';

			return { q, priceParams };
		}

		buildUrl(q, priceParams, page) {
			const params = new URLSearchParams();
			params.set('q', q);
			params.set('type', 'product');
			params.set('options[prefix]', 'none');
			params.set('sort_by', this.sort);
			params.set('options[unavailable_products]', this.unavailable);
			params.set('section_id', this.resultsSection);
			params.set('gf_ppp', String(this.ppp));
			params.set('gf_flags', this.flags);
			params.set('page', String(page));
			Object.keys(priceParams).forEach((key) => params.set(key, priceParams[key]));
			return `${this.searchUrl}?${params.toString()}`;
		}

		async fetchResults(q, priceParams, page) {
			const response = await fetch(this.buildUrl(q, priceParams, page));
			if (!response.ok) throw new Error(`Gift finder request failed: ${response.status}`);
			const text = await response.text();
			const doc = new DOMParser().parseFromString(text, 'text/html');
			return doc.querySelector('[data-gf-results-inner]');
		}

		/* ------------------------------------------------------------------- search */

		async search(options) {
			const { q, priceParams } = this.buildQuery();
			const pinnedProducts = this.getMatchingBoostProducts();
			const pinnedProductIds = pinnedProducts.map((product) => product.id);
			const scroll = !options || options.scroll !== false;
			const historyMode = options?.historyMode;

			this.setBusy(this.submitBtn, true);
			this.resultsEl?.setAttribute('aria-busy', 'true');
			if (this.showMoreWrap) this.showMoreWrap.hidden = true;

			try {
				const inner = await this.fetchResults(q, priceParams, 1);
				const grid = inner?.querySelector('[data-gf-grid]');
				const count = parseInt(inner?.dataset.resultsCount || '0', 10);

				if (!inner || count === 0 || !grid || grid.children.length === 0) {
					if (this.resultsEl) this.resultsEl.innerHTML = '';
					this.showState('no-results');
					this.currentParams = null;
					this.writeUrlState(historyMode);
					return;
				}
				this.pinBoostProducts(grid, pinnedProducts);

				this.showState('results');
				if (this.resultsEl) {
					this.resultsEl.innerHTML = inner.innerHTML;
					this.applyLoadMoreLabel(this.resultsEl);
					this.reinit(this.resultsEl);
				}

				this.currentParams = { q, priceParams, pinnedProductIds };
				this.currentPage = parseInt(inner.dataset.currentPage || '1', 10) || 1;
				this.totalPages = parseInt(inner.dataset.totalPages || '1', 10) || 1;
				this.updateShowMore();

				this.writeUrlState(historyMode);
				if (scroll) this.scrollToResults();
			} catch (error) {
				this.showState('error');
				if (this.resultsEl) this.resultsEl.innerHTML = '';
				this.currentParams = null;
				this.currentPage = 1;
				this.totalPages = 1;
				console.error('[gift-finder]', error);
			} finally {
				this.setBusy(this.submitBtn, false);
				this.resultsEl?.setAttribute('aria-busy', 'false');
			}
		}

		onDelegatedClick(e) {
			const button = e.target.closest('[data-gf-load-more]');
			if (!button || !this.contains(button)) return;
			e.preventDefault();
			this.loadMore(button);
		}

		async loadMore(button) {
			if (!this.currentParams || this.currentPage >= this.totalPages) return;

			this.setBusy(button, true);

			try {
				const { q, priceParams, pinnedProductIds } = this.currentParams;
				const inner = await this.fetchResults(q, priceParams, this.currentPage + 1);
				const newGrid = inner?.querySelector('[data-gf-grid]');
				const grid = this.resultsEl?.querySelector('[data-gf-grid]');
				if (!inner || !newGrid || !grid) return;
				this.removeBoostDuplicates(newGrid, pinnedProductIds);

				const marker = grid.lastElementChild;
				grid.insertAdjacentHTML('beforeend', newGrid.innerHTML);
				this.currentPage += 1;

				const wrap = this.resultsEl.querySelector('[data-gf-load-more-wrap]');
				const nextWrap = inner.querySelector('[data-gf-load-more-wrap]');
				if (wrap) {
					if (nextWrap) {
						wrap.replaceWith(nextWrap);
						this.applyLoadMoreLabel(this.resultsEl);
					} else {
						wrap.remove();
					}
				}

				this.reinitAfter(grid, marker);
				this.updateShowMore();
			} catch (error) {
				console.error('[gift-finder]', error);
			} finally {
				this.setBusy(button, false);
			}
		}

		/* -------------------------------------------------------------------- view */

		setBusy(button, busy) {
			if (!button) return;
			button.disabled = busy;
			button.classList.toggle('is-busy', busy);
		}

		showState(state) {
			this.resultState = state;
			if (this.emptyEl) this.emptyEl.hidden = state !== 'empty';
			if (this.noResultsEl) this.noResultsEl.hidden = state !== 'no-results';
			if (this.errorEl) this.errorEl.hidden = state !== 'error';
			this.updateShowMore();
		}

		updateShowMore() {
			if (!this.showMoreWrap || !this.showMoreLink) return;
			const selected = this.dropdowns[0]?.options.find((option) => option.getAttribute('aria-selected') === 'true');
			const collectionUrl = selected?.dataset.gfCollectionUrl;
			const hasResultsState = this.resultState === 'results' || this.resultState === 'no-results';
			const hasLoadMore = Boolean(this.resultsEl?.querySelector('[data-gf-load-more]'));
			this.showMoreWrap.hidden = !hasResultsState || hasLoadMore || !collectionUrl;
			if (collectionUrl) {
				this.showMoreLink.setAttribute('href', collectionUrl);
			} else {
				this.showMoreLink.removeAttribute('href');
			}
		}

		applyLoadMoreLabel(scope) {
			if (!this.loadMoreLabel) return;
			const label = scope.querySelector('.gift-finder__load-more-label');
			if (label) label.textContent = this.loadMoreLabel;
		}

		scrollToResults() {
			const target = this.resultsPanel;
			if (!target) return;
			const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
		}

		/* ------------------------------------------------------------------ re-init */

		reinitAfter(grid, marker) {
			const added = [];
			let node = marker ? marker.nextElementSibling : grid.firstElementChild;
			while (node) {
				added.push(node);
				node = node.nextElementSibling;
			}
			added.forEach((el) => this.bindCardButtons(el));
			this.dispatchReinit();
		}

		reinit(scope) {
			if (!scope) return;
			this.bindCardButtons(scope);
			this.dispatchReinit();
		}

		bindCardButtons(scope) {
			scope.querySelectorAll('[data-add-to-lists-button]').forEach((button) => {
				if (button.dataset.gfBound === 'true') return;
				button.dataset.gfBound = 'true';
				// Claim the button so the delegated "dynamic button" fallback in lists.js
				// doesn't also fire open() for the same click.
				button.setAttribute('data-lists-initialized', 'true');
				button.addEventListener('click', () => {
					document.dispatchEvent(new CustomEvent('trigger::addToLists', { detail: { button } }));
				});
			});
		}

		dispatchReinit() {
			document.dispatchEvent(new Event('trigger::initLists'));

			try {
				if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
					window.yotpoWidgetsContainer.initWidgets();
				}
			} catch (error) {
				console.error('[gift-finder] yotpo', error);
			}
		}

		/* --------------------------------------------------------------- url state */

		urlKey(i) {
			return `gf_${this.instanceId}_${i + 1}`;
		}

		writeUrlState(historyMode) {
			if (!this.dropdowns.length) return;
			const params = new URLSearchParams(window.location.search);
			this.dropdowns.forEach((dd, i) => {
				const index = dd.options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
				params.set(this.urlKey(i), String(index > -1 ? index : 0));
			});
			const query = params.toString();
			const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;

			/* Each user-initiated search pushes a new entry so Back/Forward can step
			   through prior searches; the initial URL restore on load replaces instead
			   of adding a spurious entry, and popstate syncs without re-pushing. */
			if (historyMode === 'replace') {
				history.replaceState({ giftFinder: true }, '', url);
			} else {
				history.pushState({ giftFinder: true }, '', url);
			}
		}

		restoreFromUrl() {
			if (!this.dropdowns.length) return;
			const params = new URLSearchParams(window.location.search);
			let restored = false;

			this.dropdowns.forEach((dd, i) => {
				const raw = params.get(this.urlKey(i));
				if (raw === null) return;
				const index = parseInt(raw, 10);
				if (Number.isNaN(index) || !dd.options[index]) return;
				this.select(dd, index, { refocus: false });
				restored = true;
			});

			if (restored) this.search({ scroll: false, historyMode: 'replace' });
		}

		onPopState() {
			if (!this.dropdowns.length) return;
			const params = new URLSearchParams(window.location.search);
			const hasAnyKey = this.dropdowns.some((dd, i) => params.get(this.urlKey(i)) !== null);

			this.dropdowns.forEach((dd, i) => {
				const raw = params.get(this.urlKey(i));
				const index = raw === null ? 0 : parseInt(raw, 10);
				const safeIndex = !Number.isNaN(index) && dd.options[index] ? index : 0;
				this.select(dd, safeIndex, { refocus: false });
			});

			if (hasAnyKey) {
				this.search({ scroll: false, historyMode: 'replace' });
			} else {
				this.showState('empty');
				if (this.resultsEl) this.resultsEl.innerHTML = '';
				this.currentParams = null;
			}
		}
	}

	customElements.define('gift-finder', GiftFinder);
}
